import { useState, useEffect } from 'react';
import type { NodeKind, NodeOption } from '@/data/nodeOptions';
import { getAccessToken } from '@/lib/auth';

/**
 * PROJ-085 Wave 1: Renamed from useWorkflowNodeOptions
 * Hook to fetch visual nodes from API and convert them to NodeOptions
 * for the canvas menu
 */
export function useVisualNodeOptions() {
  const [nodeOptions, setNodeOptions] = useState<NodeOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    async function fetchNodes() {
      try {
        setLoading(true);
        setError(null);

        // Fetch system node definitions from public catalog API
        // 2-Container Architecture: Backend serves all endpoints at /api/v1
        // PROJ-085: Updated endpoint from /workflow-nodes to /visual-nodes
        const API_BASE = (import.meta.env.VITE_API_URL || '/api/v1').replace(/\/$/, '');
        const headers: HeadersInit = {};
        const token = getAccessToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(`${API_BASE}/visual-nodes/catalog?limit=100`, {
          headers,
          credentials: 'include',
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch visual nodes: ${response.statusText}`);
        }

        const data = await response.json();
        if (controller.signal.aborted) return;
        const systemNodes: any[] = data.success ? data.data : [];

        // Map node kind codes to NodeKind types
        const kindMapping: Record<string, NodeKind> = {
          'TRIGGER': 'trigger',
          'ACTION': 'action',
          'TRANSFORM': 'action',
          'LOGIC': 'action',
          'OUTPUT': 'action',
          'INTEGRATION': 'action',
        };

        // Map system node definitions to NodeOptions
        const options = (systemNodes || []).reduce<NodeOption[]>((acc: NodeOption[], node: any) => {
          // Determine node kind from type (kind_code from backend)
          const typeUpper = (node.type || '').toUpperCase();
          const nodeKind = kindMapping[typeUpper] || 'action';

          // Extract icon - use blobUrl for SVG rendering, fallback to emoji
          const iconUrl = node.icon?.blobUrl || null;
          const icon = iconUrl || node.product?.name?.charAt(0) || '📦';

          // Description with sensible fallbacks
          const description = node.description || node.category || node.type || 'action';

          acc.push({
            id: node.id,
            type: nodeKind,
            icon,
            iconUrl, // Add iconUrl for SVG rendering
            title: node.label || node.code || 'Unnamed Node',
            description,
            category: node.category || 'Otros',
            colorTheme: node.colorTheme,
            nodeData: {
              code: node.code,
              product: node.product,
              brand: node.brand,
            },
          });

          return acc;
        }, []);

        setNodeOptions(options);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        console.error('Error fetching visual node options:', err);
        setError(err as Error);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }

    fetchNodes();
    return () => { controller.abort(); };
  }, []);

  return { nodeOptions, loading, error };
}
